import React from 'react';
import moment from 'moment';
import {
    Badge,
    IconButton,
    Grid,
    Tooltip,
    Typography
} from '@mui/material';
import {
    GridColumnMenu,
    getGridStringOperators,
    getGridSingleSelectOperators,
} from '@mui/x-data-grid-pro';
import {
    green,
    trakrefBlue,
    red,
} from '../../../color_utils';
import { renderWeight } from '../../../Utils/weight_utils';
import {
    ExcelIcon,
    ImageIcon,
    PDFIcon,
} from '../../../Icons';
import UserInfo from '../../../UserInfo';
import Routes from '../../../routes';
import {trakrefFontFamily, trakrefFontFamilySemiBold} from "../../../utils";
import PrimaryButton from '../PrimaryButton';

export const determineBadgeColor = (status) => {
    let color = null
    const statusToLowerCase = status?.toLowerCase();
    switch (statusToLowerCase) {
        case 'work completed':
        case 'operational':
            color = green
            break;
        case 'cancelled':
        case 'deactivated':
            color = '#D1D9DE'
            break;
        case 'open':
            color = '#FFBB00'
            break;
        case 'closed':
            color = trakrefBlue;
            break;
        case 'deleted':
            color = red;
            break;
        default:
            color = '#8BDC54'
            break;
    }
    return color;
};

export const exportType = {
    pdf: "pdf",
    csv: "csv",
    xlsm: "xlsm",
    xlsx: "xlsx"
};

const batchUpdateExportServiceEvents = async () => {
    fetch(Routes.ServiceEvents.GetBatchUpdateExport(), {
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        },
    })
        .then(async (response) => {
            console.log('response', response);
            const URL = await Promise.resolve(response.json());
            window.open(URL, "_blank").focus();
        })
        .catch(err => console.log('Error!', err));
};

const batchUpdateImportServiceEvents = async (file) => {
    const formattedFile = {
        fileName: file.file.name,
        fileBytes: file.data,
        contentType: file.file.type
    };
    console.log('formattedFile', formattedFile);
    fetch(Routes.ServiceEvents.BulkUpdate(), {
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        },
        method: "POST",
        body: JSON.stringify(formattedFile),
    })
        /* .then(async (response) => {
            console.log('response', response);
            const URL = await Promise.resolve(response.json());
            window.open(URL, "_blank").focus();
        }) */
        .catch(err => console.log('Error!', err));
};

export const renderStatusBadge = (statusProperty) => {
    const badgeColor = determineBadgeColor(statusProperty);
    return (
        <Tooltip
            title={statusProperty}
        >
            <Badge
                variant="dot"
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                sx={{
                    padding: '0 0 0 10%',
                    '& .MuiBadge-badge': {
                        top: '50%',
                        borderRadius: '50%',
                        backgroundColor: badgeColor,
                        color: badgeColor,
                    }
                }}
                badgeContent=""
            >
            </Badge>
        </Tooltip>
    );
};

export const determineLeakRateIcon = (epaAnnualizedLeakRate, assetTypeId) => {
    if (
        (epaAnnualizedLeakRate > 0.1 && assetTypeId === 31)
        || (epaAnnualizedLeakRate > 0.2 && assetTypeId === 22)
        || (epaAnnualizedLeakRate > 0.3 && assetTypeId === 63)
        || (epaAnnualizedLeakRate > 999999999.0)
    ) {
        return true;
    }
    return false;
};

export const getFileIcon = (fileName) => {
    const fileType = fileName.split('.').pop();
    const imageTypes = ["png", "jpg", "jpeg"];

    if (imageTypes.includes(fileType)) {
        return ImageIcon;
    }
    if (fileType === 'pdf') {
        return PDFIcon;
    }
    if (fileType === 'xls' || fileType === 'csv') {
        return ExcelIcon;
    }
    // default case
    return ImageIcon;
};

export const convertDateTime = dateTime => moment(dateTime).utc().format('MM/DD/YYYY');

export const convertBoolean = boolean => boolean === true ? "Yes" : "No";

const getFileName = (type, customFileName) => {
    switch (type) {
        case exportType.pdf:
            return customFileName ? `${customFileName}_${Date.now()}.pdf` : `ServiceEvents_${Date.now()}.pdf`;
        case exportType.xlsm:
            return `${customFileName}.xlsm`;
        default:
            return `${customFileName}.csv`;
    }
};

export const workOrderDeleted = (column, row, link) => {
    if (column.props.isWorkOrder && UserInfo.isSystemAdministrator() === true && row[column.props.deletedProperty].toUpperCase() === 'YES') {
        return link;
    }

    return null;
};

export const getRenderValue = (type, value) => {
    let returnValue = value;
    switch (type) {
        case "dateTime":
            returnValue = value == null ? "" : convertDateTime(value);
            break;
        case "bool":
            returnValue = convertBoolean(value);
            break;
        case "percentage":
            returnValue = value === null || value === "" || value === "N/A" ? value : parseFloat(value).toFixed(UserInfo.getInstance(UserInfo.getInstanceID()).weightPrecision) + "%";
            break;
        case "weight":
            returnValue = renderWeight(value);
            break;
        default:
            break;
    }
    return returnValue;
};

export const getDefaultPage = (url, defaultPage) => {
    const possiblePage = sessionStorage.getItem(url + "page" + UserInfo.instanceId);

    if (possiblePage) {
        return JSON.parse(possiblePage);
    }

    return defaultPage;
};

export const getDefaultSortBy = (url, defaultSort) => {
    const sortKey = `${url}sort${UserInfo.instanceId}`;

    if (defaultSort) {
        sessionStorage.setItem(sortKey, JSON.stringify(defaultSort));
    }

    return JSON.parse(sessionStorage.getItem(sortKey));
};

export const batchUpdateExport = async (batchUpdateType) => {
    if (!batchUpdateType) return null;

    const batchUpdateFunctionTypes = {
        serviceEvents: batchUpdateExportServiceEvents
    };

    const exportFunction = batchUpdateFunctionTypes[batchUpdateType];

    return exportFunction();
};

export const batchUpdateImport = async (batchUpdateType, excelFile) => {
    if (!batchUpdateType) return null;

    const batchUpdateImportFunctionTypes = {
        serviceEvents: () => batchUpdateImportServiceEvents(excelFile)
    };

    const exportFunction = batchUpdateImportFunctionTypes[batchUpdateType];

    return exportFunction();
};

export const getFilterOptions = (field, options) => {
  const seen = new Set();
  const result = [];

  for (const f of options) {
    if (f.property === field && f.name && f.value && !seen.has(f.value)) {
      seen.add(f.value);
      result.push({ value: f.value, label: f.name });
    }
  }
  return result;
};

export const dataGridContainerProps = { xs: 12, md: 12, lg: 12, marginTop: { xs: 1, md: 5 } };

// Generate a filter object based on how the server expects filters
export const generateFilterModel = (filterItems) => {
    // Filters exist - construct filter array
    if (Array.isArray(filterItems) && filterItems.length > 0) {
        // 'value' is initially undefined when the user selects the column to filter on
         return filterItems
            .filter(({ value }) => {
                // Ensure that filter value is a string..
                // ..and at least 1 character has been entered
                if (typeof value === 'string' && value.length > 0) {
                    return true;
                }

                // Both 'from' & 'to' must have values for date range filter
                if (Array.isArray(value) && value[0] && value[1]) {
                    return true;
                }

                return false;
            })
            .map(({ field, value = '', operator }) => {
                const filter = { property: field, value };

                switch (operator) {
                    case 'from-to':
                        let from = value[0];
                        let to = value[1];
                        
                        // this is only used if pushing a user to a page with a date filter applied
                        if (typeof value === 'string') {
                            // ex '01/15/2025-01/16/2025'
                            from = value.split('-')[0]
                            to = value.split('-')[1]
                        }
                        
                        filter.value = `${moment(from).format('L')}-${moment(to).format('L')}`;
                        return filter;
                    case 'between':
                        const lowerRange = value[0];
                        const upperRange = value[1];
                        filter.value = `${lowerRange}-${upperRange}`;
                        return filter;

                    // Add more filter operator cases here, as they come up
                
                    default:
                        return filter;
                }
            });
    }

    // No filters exist
    return [];
};

export const generateSortModel = (newSortModel) => {
    // newSortModel will be empty when removing all sorting
    if (newSortModel.length === 0) return null;

    const [{ field, sort }] = newSortModel;
    return sort === 'desc' ? `${field}_desc` : `${field}`;
};


// Extract sort from saved table state in localStorage
// This ensures the data fetch matches the visual sort icons on remount
export const getSortFromSavedState = (localStorageKey, fallbackSort) => {
    try {
        const savedState = JSON.parse(localStorage.getItem(localStorageKey));
        if (savedState?.sorting?.sortModel?.length > 0) {
            return generateSortModel(savedState.sorting.sortModel);
        }
    } catch (e) {
        // If parsing fails, fall back
    }
    return fallbackSort;
};
export const parseSortByToSortModel = (sortBy) => {
    if (!sortBy) return [];

    const isDescending = sortBy.endsWith('_desc');
    const field = isDescending ? sortBy.slice(0, -5) : sortBy;

    return [{ field, sort: isDescending ? 'desc' : 'asc' }];
};

export const generateColumn = (column, filterOptions) => {
    const baseColumn = {
        ...column,
        minWidth: 190,
    };
    if (column && 'type' in column && column.type === 'singleSelect') {
        return {
            ...baseColumn,
            valueOptions: Array.isArray(column.valueOptions) ? column.valueOptions : getFilterOptions(column.field, filterOptions),
        };
    }

    return baseColumn;
};

// Save snapshot of table state when:
// filters are applied
// columns are shown/hidden
// column reordering occurs
// NOTE: timeout is required to allow internal state of table to update and export
export const saveTableState = (gridApiRef, localStorageName) => {
    setTimeout(() => {
        const currentTableState = gridApiRef.current.exportState();
        console.log('exported table state saved to localStorage', currentTableState)
        localStorage.setItem(localStorageName, JSON.stringify({
            ...currentTableState,
            // Ensure that saved snapshot has a closed preferencePanel
            preferencePanel: { ...currentTableState.preferencePanel, open: false }
        }));
    }, 500);
};

export const ColumnMenu = (props) => (
    <GridColumnMenu
        {...props}
        slots={{
            columnMenuColumnsItem: null,
        }}
    />
);

// Hide action column from column menu
export const getTogglableColumns = (columns, field = '__detail_panel_toggle__') => (
    columns
        .filter((column) => ![field].includes(column.field) && !column.hiddenFromTogglable)
        .map((column) => column.field)
);

// Until we determine what additional filter operators we want to develop,
// for now we are only moving forward with the "contains" operator for string,
// and "is" operator for dropdown select
const getDefaultStringFilterOperators = () => (
    getGridStringOperators().filter(operator => operator.value === "contains")
);

const getDefaultSelectFilterOperators = () => (
    getGridSingleSelectOperators().filter(operator => operator.value === "is")
);

export const text_commonCellProps = {
    type: 'text',
    flex: 1,
    filterOperators: getDefaultStringFilterOperators()
};

export const select_commonCellProps = {
    type: 'singleSelect',
    flex: 1,
    filterOperators: getDefaultSelectFilterOperators()
};

export const NoData = ({
    noTableDataIcon,
    noResultText,
    noResultText2,
    noResultText3,
    tableRequest,
    MUIButtonText,
    MUIButtonOnClick,
}) => (
    <Grid container>
        <Grid item xs={12} align='center'>
            <img
                style={{ paddingTop: '4%' }}
                src={noTableDataIcon}
                alt={noResultText || "No results to display. Reset filters to see more results"}
            />
        </Grid>
        <Grid item xs={12}>
            {
                noResultText ? (
                    <Typography
                        variant='h5'
                        align='center'
                        style={{
                            marginTop: '2%',
                            paddingBottom: noResultText2 ? '.5%' : '2%',
                            color: '#1A173B',
                            fontFamily: trakrefFontFamilySemiBold,
                        }}
                    >
                        {noResultText}
                    </Typography>
                ) : (
                    <Typography
                        variant='h5'
                        align='center'
                        style={{
                            marginTop: '2%',
                            paddingBottom: '2%',
                            color: '#1A173B',
                            fontFamily: trakrefFontFamilySemiBold
                        }}
                    >
                        No results to display. Reset filters to see more results
                    </Typography>
                )
            }
        </Grid>
        {
            (noResultText2 && !noResultText3) ? (
                    <Grid item xs={12}>
                        <Typography
                            variant='h5'
                            align='center'
                            style={{
                                paddingBottom: '2%',
                                color: '#1A173B',
                                fontSize: '1.25rem',
                                fontFamily: trakrefFontFamily
                            }}
                        >
                            {noResultText2}
                        </Typography>
                    </Grid>
                )
                : (noResultText2 && tableRequest && !('filters' in tableRequest && tableRequest.filters.length)) ? (
                    <Grid item xs={12}>
                        <Typography
                            variant='h5'
                            align='center'
                            style={{
                                paddingBottom: '2%',
                                color: '#1A173B',
                                fontSize: '1.25rem',
                                fontFamily: trakrefFontFamily
                            }}
                        >
                            {noResultText2}
                        </Typography>
                    </Grid>
                ) : (
                    (noResultText3 && tableRequest && !!('filters' in tableRequest && tableRequest.filters.length)) && (
                        <Grid item xs={12}>
                            <Typography
                                variant='h5'
                                align='center'
                                style={{
                                    paddingBottom: '2%',
                                    color: '#1A173B',
                                    fontSize: '1.25rem',
                                    fontFamily: trakrefFontFamily
                                }}
                            >
                                {noResultText3}
                            </Typography>
                        </Grid>
                    )
                )
        }
       {MUIButtonText && MUIButtonOnClick && (
            <Grid item xs={12} align="center" sx={{ marginBottom: '3%', px: { xs: 1, sm: 0 } }}>
                <PrimaryButton
                    buttonText={MUIButtonText}
                    onClick={MUIButtonOnClick}
                    disabled={UserInfo.isReadOnly()}
                    sx={{ width: { xs: '100%', sm: '20%' } }}
                />
            </Grid>
        )}
    </Grid>
);

export const getLocalStorageKey = (url) => `${url}/columns/${UserInfo.instanceId}/${UserInfo.userId}`;

export const setFiltersPushUser = ({
   destinationTableUrl,
   destinationPath,
   items,
   navigate
}) => {
    const localStorageKey = getLocalStorageKey(destinationTableUrl);
    const tableState = {
        filter: { filterModel: { items } },
    };
    localStorage.setItem(localStorageKey, JSON.stringify(tableState));
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    return navigate.push(destinationPath);
};

export const EmptyActionIcon = () => (
    <IconButton disabled sx={{ width: "30px", height: "30px", padding: "0px", margin: "0px" }} />
);

export const validateAndCleanCachedState = (localStorageKey, currentColumns) => {
    try {
        const cachedStateStr = localStorage.getItem(localStorageKey);
        if (!cachedStateStr) return null;

        const cachedState = JSON.parse(cachedStateStr);
        if (!cachedState) return null;

        // Get current field names
        const currentFields = new Set(currentColumns.map(col => col.field));

        // Validate cached columns
        const cachedFields = cachedState?.columns?.orderedFields || [];
        const invalidColumns = cachedFields.filter(field => !currentFields.has(field));

        // Validate sort model
        const sortField = cachedState?.sorting?.sortModel?.[0]?.field;
        const invalidSort = sortField && !currentFields.has(sortField);

        // Validate filter model
        const filterItems = cachedState?.filter?.filterModel?.items || [];
        const invalidFilters = filterItems.filter(item => !currentFields.has(item.field));

        // Check for mismatches
        if (invalidColumns.length > 0 || invalidSort || invalidFilters.length > 0) {
            localStorage.removeItem(localStorageKey);
            return null;
        }

        // Cache is valid
        return cachedState;

    } catch (error) {
        console.error('DataGrid: Failed to parse cached state, clearing cache', {
            error: error.message,
            localStorageKey
        });
        // Clear corrupted cache
        localStorage.removeItem(localStorageKey);
        return null;
    }
};